'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, type FC } from 'react'

import { useModal } from 'lib/components/Modal/Modal'

import { useGetMicroSiteApi } from '@/api/useGetMicroSiteApi'
import { useCart } from '@/app/book/hooks/useCart'
import useMicroSiteStore from '@/stores/useMicroSiteStore'
import { BookingFlowLayoutProps } from '@/types/bookingFlow'

import SessionExpiredModal, { SESSION_EXPIRED_MODAL_ID } from './components/SessionExpiredModal'
import { useSessionExpiration } from './hooks/useSessionExpiration'
import { useStep } from './hooks/useStep'
import BookingFlowTemplate from './layouts/BookingFlow'
import Sidebar from './layouts/Sidebar'
import { routes } from './routes'
import { steps } from './steps'
import useCartStore from './stores/useCartStore'
import useLayoutPropsStore from './stores/useLayoutPropsStore'
import useShipmentStore from './stores/useShipmentStore'
import useStepsStore from './stores/useStepsStore'

const BookLayoutClient: FC<BookingFlowLayoutProps> = ({ children }) => {
  const {
    topContents,
    bottomContents,
    hideCurrentStep,
    hideSteps,
    showLoadingIcon,
    sidebarCTADisabled,
    sidebarCTALabel,
    sidebarMobileCTALabel,
    sidebarCTAOnClick,
  } = useLayoutPropsStore()
  const { clearCart } = useCartStore()
  const { clearShipment } = useShipmentStore()
  const { clearSteps, steps: stepsFromStore, hasHydrated } = useStepsStore()
  const { number } = useStep()
  const { updateCart } = useCart()
  const searchParams = useSearchParams()
  const { openDialog } = useModal()

  const handleSessionExpired = useCallback(() => {
    clearCart()
    clearShipment()
    clearSteps()
    openDialog(SESSION_EXPIRED_MODAL_ID)
  }, [clearCart, clearShipment, clearSteps, openDialog])

  useSessionExpiration({ onExpired: handleSessionExpired, hasHydrated: !!hasHydrated })

  const { data: microSite } = useGetMicroSiteApi(searchParams.get('partner'))

  const { setMicroSite, clearMicroSite } = useMicroSiteStore()

  const stepsExtended = steps.map((step) => {
    const completed =
      stepsFromStore.find(({ number }) => number === step.number)?.completed || false

    const path =
      step.number === 2
        ? '/book/travelers'
        : routes.find(({ stepNumber }) => stepNumber === step.number)?.path

    step.completed = completed
    step.path = path

    return step
  })
  /**
   * The BookingFlowTemplate only renders children if the curent step is within
   * the steps array; if it's not it will render another template. In order to
   * get the stepper to show all steps as complete on the review page we need
   * to pass the number of steps as greater than the number of steps in the
   * steps array. Additionally, we need to get the review page to render as a
   * child. This is how we do that.
   */
  const currentStep = Math.min(stepsExtended.length, number)

  const clearCartHandler = useCallback(() => {
    clearCart()
    clearShipment()
    clearSteps()
    // A full page load is needed to clear the sidebar state
    if (typeof window !== 'undefined') window.location.href = '/book/ship'
  }, [clearCart, clearShipment, clearSteps])

  useEffect(() => {
    if (microSite) {
      setMicroSite(microSite)
    } else {
      clearMicroSite()
    }
  }, [microSite, clearMicroSite, setMicroSite])

  // Redirect to the first step if there are no steps in the store
  useEffect(() => {
    // wait for the persisted state to come back
    if (!hasHydrated) return

    // now you know `stepsFromStore` is the real, persisted array (even if empty)
    if (number > 0 && stepsFromStore.length === 0) {
      const path = routes[0].path || '/book/ship'

      // Hard navigation — a router.push soft-nav into the statically-exported
      // /book/ship can render the raw RSC payload (GSE-11866). Skip when
      // already on the first step: a fresh visit to /book/ship has an empty
      // steps store, and reloading would loop.
      if (typeof window !== 'undefined' && window.location.pathname !== path) {
        window.location.href = `${path}?${searchParams.toString()}`
      }
    }

    if (stepsFromStore.length > 0 && number === 2) updateCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, number, stepsFromStore, searchParams])

  const handleCreateNewShipment = useCallback(() => {
    // Full page load to reset all state cleanly
    if (typeof window !== 'undefined') window.location.href = '/book/ship'
  }, [])

  return (
    <>
      <SessionExpiredModal onCreateNewShipment={handleCreateNewShipment} />
      <BookingFlowTemplate
        hideCurrentStep={hideCurrentStep ?? false}
        hideSteps={hideSteps ?? false}
        steps={stepsExtended}
        currentStep={currentStep}
        topContents={topContents}
        bottomContents={bottomContents}
        sidebar={
          <Sidebar
            cartClearOnClick={clearCartHandler}
            ctaDisabled={sidebarCTADisabled}
            mobileCtaLabel={sidebarMobileCTALabel}
            ctaLabel={sidebarCTALabel}
            ctaOnClick={sidebarCTAOnClick}
            showLoadingIcon={showLoadingIcon}
          />
        }
      >
        {children}
      </BookingFlowTemplate>
    </>
  )
}

export default BookLayoutClient
